import { Handle, Position } from '@xyflow/react'
import { Database, X, FileJson, File, Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface DataNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

const sourceTypeIcons: Record<string, React.ReactNode> = {
  database: <Database size={14} />,
  file: <File size={14} />,
  api: <Link2 size={14} />,
  json: <FileJson size={14} />,
}

export default function DataNode({ data, selected, id }: DataNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const sourceType = String(data.config?.type || 'unknown')
  const sourceIcon = sourceTypeIcons[sourceType] || <Database size={14} />

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-48 transition-all ${
        selected
          ? 'border-agent-green ring-2 ring-agent-green/30 shadow-lg'
          : 'border-agent-green/50 hover:border-agent-green'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Database size={16} className="text-agent-green flex-shrink-0" />
          <div className="font-bold text-sm text-gray-900 truncate">
            {data.label || t('build.source')}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-gray-600 mb-2 bg-gray-50 rounded p-2">
        {data.config?.source && (
          <div className="truncate text-gray-700">
            {String(data.config.source)}
          </div>
        )}
        {data.config?.description && (
          <div className="text-gray-600 text-xs truncate">
            {String(data.config.description)}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
          {sourceIcon as React.ReactNode}
          {sourceType}
        </span>
        {data.config?.recordCount && (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
            {t('build.recordsCount', { count: Number(data.config.recordCount) })}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
